import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import utc from 'dayjs/plugin/utc';
import { useFetchDashboardTransactions } from '../../hooks/useFetchTransactions';
import { useUser } from '../../context/UserContext';
import { ROUTES, SystemConstants } from '../../constants/constants';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { useTranslation } from 'react-i18next';
import { ITransaction } from '../../interface/transaction';
import { MatterStatus } from '../../constants/matter';
import { IWeeklyDataRow, weeklyDataRow } from '../../constants/transactionUtility';
import { IMatter } from '../../interface/matter';
import Table, { TableProps, TableRef } from 'antd/es/table';
import { Button } from '../Ant';
import { Plus, DocumentTextEmpty } from '../icons';
import { Flex } from 'antd';
import RenderTransactionRestrictions from '../dashboard/addTransaction/renderTransactionRestrictions';
import SkeletonTable from '../common/skeleton/skeletonTable';
import { useTransaction } from '../../context/TransactionContext';
import { IPrincipalCompany } from '../../interface/principalCompany';
import { IPrincipalPrivatePerson } from '../../interface/principalPrivatePerson';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useLocation } from 'react-router-dom';
import NoData from '../dashboard/noData';
import { useQueryClient } from '@tanstack/react-query';
import { TFunction } from 'i18next';
import { useScrollCurrentDayIntoView } from '../../hooks/useScrollCurrentDayIntoView';
import { formatter, usesCommaDecimalSeparator } from '../../constants/utilityFunctions';
import { getVisibleWeekDays } from '../../helpers/timesheetHelper';

dayjs.extend(utc);

// Cell values are already localised ("8,50" in fi/sv), so the separator has to be normalised
// before parsing or Number.parseFloat truncates at the comma.
const parseCellHours = (value?: string | null): number => {
  const hours = Number.parseFloat(value?.replaceAll(',', '.') ?? '');
  return Number.isNaN(hours) ? 0 : hours;
};

const formatTotalHours = (hours: number): string => {
  const formatted = hours.toFixed(2);
  return usesCommaDecimalSeparator() ? formatted.replaceAll('.', ',') : formatted;
};

type CellData = {
  value: string | null;
  count?: number;
};

type StaticMatterFields = {
  key: string;
  matter?: IMatter;
  principalCompany?: IPrincipalCompany;
  principalPrivatePerson?: IPrincipalPrivatePerson;
  matterName?: string;
  description?: string;
  type: 'total' | 'matter';
};

export type MatterRecord = StaticMatterFields & {
  [date: `${number}-${number}-${number}`]: CellData;
};

interface HoursCellProps {
  value?: string;
  count?: number;
  onHourClick?: () => void;
  onPlusClick?: () => void;
  isTotal?: boolean;
  isCurrentDay?: boolean;
  t: TFunction;
}

const HoursCell = ({
  value,
  count,
  onHourClick,
  onPlusClick,
  isTotal,
  isCurrentDay,
  t,
}: HoursCellProps) => {
  if (isTotal) {
    return (
      <p className={isCurrentDay ? 'current-day-total' : ''}>
        {value} {t('common:h')}
      </p>
    );
  }

  let hoursContent: React.ReactNode = null;
  if (count !== undefined) {
    hoursContent = (
      <Flex vertical gap={2} align="center" className="hour-wrapper">
        <Button type="text" className="hours-clickable" onClick={onHourClick}>
          ({count < 10 ? `0${count}` : count})
        </Button>
        <p>
          {value} {t('common:h')}
        </p>
      </Flex>
    );
  }

  return (
    <div className="cell-content" data-testid="hours-cell">
      {hoursContent}
      <Button
        aria-label={t('transaction:addTransaction')}
        data-testid="plus-button"
        icon={<Plus />}
        onClick={onPlusClick}
        className="plus-icon-button"
        type="text"
      />
    </div>
  );
};

const Timesheet = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { user } = useUser();
  const { push } = useNavigationStack();
  const { updateTransaction } = useTransaction();
  const tableRef = useRef<TableRef>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedTransaction, setSelectedTransaction] = useState<ITransaction>();
  const [temporaryMatter, setTemporaryMatter] = useState<IMatter | undefined>(
    location?.state?.matter,
  );
  const currentDate = useMemo(() => dayjs().startOf('day').format('YYYY-MM-DDTHH:mm:ss'), []);

  const nineDaysAgoDate = useMemo(
    () => dayjs().subtract(10, 'day').startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
    [dayjs().day()],
  );
  const {
    data: transactionList,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
    isLoading: isLoadingTransactions,
  } = useFetchDashboardTransactions({
    relations: ['matter', 'principalCompany', 'principalPrivatePerson', 'unit'],
    extraFilters: {
      transactionUserId: user?.id ?? '',
      transactionDate: `$btw:${nineDaysAgoDate},${currentDate}`,
      unitId: SystemConstants.UNIT_HOUR,
    },
    sortBy: ['transactionDate:DESC'],
    enabled: !!user?.id,
  });
  const billableTransactions: ITransaction[] = useMemo(() => {
    const data = transactionList.filter(
      (transaction) => transaction.matter?.status === MatterStatus.ACTIVE,
    );

    if (searchValue) {
      return data.filter(
        (transaction) =>
          transaction.matter?.subject.toLowerCase().includes(searchValue.toLowerCase()) ||
          transaction.matter.matterNumber
            .toString()
            .toLowerCase()
            .includes(searchValue.toLowerCase()) ||
          transaction.principalCompany?.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          transaction.principalPrivatePerson?.fullName
            ?.toLowerCase()
            .includes(searchValue.toLowerCase()),
      );
    }

    return data;
  }, [transactionList, searchValue]);

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const rowData = useMemo(
    () => weeklyDataRow(billableTransactions ?? [], nineDaysAgoDate, temporaryMatter, searchValue),
    [billableTransactions, nineDaysAgoDate, temporaryMatter, searchValue],
  );
  useEffect(() => {
    if (temporaryMatter && billableTransactions.some((e) => e.matterId === temporaryMatter.id)) {
      setTemporaryMatter(undefined);
    }
  }, [transactionList]);

  const getUniqueDates = (response: IWeeklyDataRow[]): string[] => {
    const dateSet = new Set<string>();
    response
      .flatMap((item) => item.rowData)
      .forEach((entry) => {
        dateSet.add(entry.date);
      });
    return Array.from(dateSet).sort((a, b) => a.localeCompare(b));
  };

  const handleHourCellClick = (date: string, record: MatterRecord) => {
    push(ROUTES.TRANSACTION_TIMESHEET_LIST, {
      matter: record?.matter,
      principalCompany: record?.principalCompany,
      principalPrivatePerson: record?.principalPrivatePerson,
      transactionDate: dayjs(date).startOf('day').format('YYYY-MM-DD'),
    });
  };

  const handleHourCellPlusClick = (date: string, record: MatterRecord) => {
    if (record?.matter) {
      setSelectedTransaction({
        transactionDate: dayjs(date).startOf('day').format('YYYY-MM-DD'),
        principalCompanyId: record?.principalCompany?.id ?? null,
        principalCompany: record?.principalCompany ?? null,
        principalPrivatePersonId: record?.principalPrivatePerson?.id ?? null,
        principalPrivatePerson: record?.matter?.principalPrivatePerson ?? null,
        matter: record?.matter,
        matterId: record?.matter?.id,
        transactionTypeId: record?.matter?.transactionTypeId ?? null,
        transactionType: record?.matter?.transactionType ?? null,
        unitId: record?.matter?.unitId ?? SystemConstants.UNIT_HOUR,
        unit: record?.matter?.unit ?? null,
      } as ITransaction);
    }
  };

  const renderDateCell = (date: string, record: MatterRecord, isCurrentDay: boolean) => {
    const cell = record[date as `${number}-${number}-${number}`];
    return (
      <HoursCell
        t={t}
        data-testid="hours-cell"
        value={cell?.value ?? '0'}
        count={cell?.count}
        isTotal={record.type === 'total'}
        isCurrentDay={isCurrentDay}
        onHourClick={() => handleHourCellClick(date, record)}
        onPlusClick={() => handleHourCellPlusClick(date, record)}
      />
    );
  };

  const buildDateColumn = (date: string) => {
    const weekday = dayjs(date).format('ddd');
    const isCurrentDay = dayjs(date).isSame(dayjs(), 'day');
    return {
      title: (
        <div
          className={`week-title-wrapper ${isCurrentDay ? 'current-day' : ''}`}
          data-testid="week-title"
        >
          <span>{t(`common:weekCalendar.${weekday.toLowerCase()}`)}</span>
          <p>{formatter.dateWithoutYear(dayjs(date).toDate())}</p>
        </div>
      ),
      dataIndex: date,
      key: date,
      width: 60,
      render: (_: unknown, record: MatterRecord) => renderDateCell(date, record, isCurrentDay),
    };
  };

  const getDynamicColumns = (uniqueDates: string[]): TableProps<MatterRecord>['columns'] => {
    const baseColumn = {
      title: t('common:timesheet.matterList'),
      dataIndex: 'matter',
      key: 'matter',
      width: 140,
      fixed: true,
      render: (_: unknown, record: MatterRecord) =>
        record.type === 'total' ? (
          <p data-testid="total-hours">{t('common:timesheet.totalHours')}</p>
        ) : (
          <div className="matter-wrapper">
            <h6 data-testid="matter-name">{record.matterName}</h6>
            <p data-testid="matter-description" className="lineclamp-1">
              {record.description}
            </p>
          </div>
        ),
    };

    const dateColumns = uniqueDates.map(buildDateColumn);
    return [baseColumn, ...dateColumns];
  };

  const populateRowDates = (row: MatterRecord, rowData: IWeeklyDataRow['rowData']): void => {
    rowData.forEach((entry) => {
      row[entry.date as `${number}-${number}-${number}`] = {
        value: entry.hours,
        count: entry.totalTransactions ?? undefined,
      };
    });
  };

  const buildMatterRow = (item: IWeeklyDataRow): MatterRecord => {
    const row: MatterRecord = {
      key: item.matter.id,
      matter: item.matter,
      principalCompany: item.principalCompany,
      principalPrivatePerson: item.principalPrivatePerson,
      matterName: item?.principalCompany?.name ?? item?.principalPrivatePerson?.fullName ?? '',
      description: item?.matter?.matterNumber + ' - ' + item?.matter?.subject,
      type: 'matter',
    };

    populateRowDates(row, item.rowData);
    return row;
  };

  const calculateDateTotal = (date: string, rows: MatterRecord[]): number => {
    return rows.reduce(
      (sum, row) => sum + parseCellHours(row[date as `${number}-${number}-${number}`]?.value),
      0,
    );
  };

  const buildTotalRow = (rows: MatterRecord[], uniqueDates: string[]): MatterRecord => {
    const totalRow: MatterRecord = {
      key: 'total',
      type: 'total',
    };

    uniqueDates.forEach((date) => {
      const total = calculateDateTotal(date, rows);
      totalRow[date as `${number}-${number}-${number}`] = {
        value: total > 0 ? formatTotalHours(total) : null,
      };
    });

    return totalRow;
  };

  const transformData = (response: IWeeklyDataRow[], uniqueDates: string[]): MatterRecord[] => {
    const rows: MatterRecord[] = response.map(buildMatterRow);
    const totalRow = buildTotalRow(rows, uniqueDates);
    return [totalRow, ...rows];
  };
  const handleAddMatter = () => {
    push(ROUTES.MATTER_SELECTION, { isFromTimesheet: true, temporaryMatter });
  };

  const handleSearch = (val: string) => {
    setSearchValue(val);
  };

  const handleRefresh = () => {
    queryClient.removeQueries({
      queryKey: ['transactions'],
    });
    return refetch();
  };

  const getRowClassName = (record: MatterRecord): string => {
    return record.type === 'total' ? 'total-row' : '';
  };

  const uniqueDates = getUniqueDates(rowData);
  const columns = getDynamicColumns(uniqueDates);
  const timeSheetData = transformData(rowData, uniqueDates);
  const isTimesheetLoading =
    isLoadingTransactions || isFetching || isFetchingNextPage || hasNextPage;
  const skeletonColumnCount = getVisibleWeekDays(nineDaysAgoDate, []).length + 1;
  const hasTimesheetRows = timeSheetData?.length > 1;

  useScrollCurrentDayIntoView(tableRef, hasTimesheetRows && !isTimesheetLoading);

  const handleSelectTransaction = (transaction?: ITransaction) => {
    if (transaction) {
      updateTransaction({
        ...transaction,
        transactionDate: transaction?.transactionDate,
        unitId: SystemConstants.UNIT_HOUR,
        matterId: transaction.matter?.id ?? '',
        matter: transaction.matter,
        principalCompanyId: transaction.principalCompany?.id ?? '',
        principalCompany: transaction.principalCompany,
        principalPrivatePersonId: transaction.principalPrivatePerson?.id ?? '',
        principalPrivatePerson: transaction.principalPrivatePerson,
        overrideWarnings: true,
        onlyHourBasedTransactionTypes: true,
      });
      push(ROUTES.ADD_EDIT_TRANSACTION);
    } else {
      setSelectedTransaction(undefined);
    }
  };
  return (
    <Flex gap={10} vertical className="h-full">
      <SearchInputWithFilters
        searchTitle={t('matter:search')}
        handleSearch={handleSearch}
        handleAdd={handleAddMatter}
        handleRefresh={handleRefresh}
        tooltipTextForAdd={t('matter:selectMatter')}
      />
      {hasTimesheetRows && !isTimesheetLoading && (
        <Table
          ref={tableRef}
          columns={columns}
          dataSource={timeSheetData}
          pagination={false}
          rowClassName={getRowClassName}
          rowHoverable={false}
          scroll={{ x: 200, y: 'calc(var(--viewport-height) - 220px)' }}
          className="timesheet-table"
        />
      )}
      {timeSheetData?.length === 1 && !isTimesheetLoading && (
        <div className="h-full d-flex justify-center items-center">
          <NoData
            header={t('transaction:noTransactions')}
            subHeader={t('common:timesheet.noRecentTransactions')}
            icon={<DocumentTextEmpty />}
          />
        </div>
      )}
      {isTimesheetLoading && <SkeletonTable columns={skeletonColumnCount} />}
      <RenderTransactionRestrictions
        transaction={selectedTransaction}
        onSelectTransaction={handleSelectTransaction}
      />
    </Flex>
  );
};

export default Timesheet;
